import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";

export type SubscriptionRow = {
  id: string;
  user_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  product_id: string;
  price_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
  created_at: string;
  updated_at: string;
};

export interface SubscriptionState {
  loading: boolean;
  subscription: SubscriptionRow | null;
  /** Pro features unlocked: active / trialing / past_due (grace) / canceled-but-still-in-period. */
  isPro: boolean;
  /** Currently in 7-day free trial. */
  isTrialing: boolean;
  /** Renewal payment failed; Stripe is retrying. Show a banner; keep access. */
  isPastDue: boolean;
  /** User canceled but still has access until period_end. */
  isCanceling: boolean;
  refresh: () => Promise<void>;
}

function deriveState(sub: SubscriptionRow | null): Omit<SubscriptionState, "loading" | "refresh"> {
  if (!sub) return { subscription: null, isPro: false, isTrialing: false, isPastDue: false, isCanceling: false };
  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end).getTime() : null;
  const inPeriod = periodEnd === null || periodEnd > Date.now();
  const isPro =
    (["active", "trialing", "past_due"].includes(sub.status) && inPeriod) ||
    (sub.status === "canceled" && inPeriod);
  return {
    subscription: sub,
    isPro,
    isTrialing: sub.status === "trialing" && inPeriod,
    isPastDue: sub.status === "past_due" && inPeriod,
    isCanceling: sub.cancel_at_period_end && inPeriod && sub.status !== "canceled",
  };
}

export function useSubscription(): SubscriptionState {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState<SubscriptionRow | null>(null);

  const fetchSub = useCallback(async () => {
    if (!user) {
      setSub(null);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", user.id)
      .eq("environment", getStripeEnvironment())
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSub((data as SubscriptionRow | null) ?? null);
    setLoading(false);
  }, [user]);

  // Keep latest fetchSub in a ref so the realtime effect doesn't re-subscribe
  // every time the callback identity changes.
  const fetchSubRef = useRef(fetchSub);
  useEffect(() => {
    fetchSubRef.current = fetchSub;
  }, [fetchSub]);

  useEffect(() => {
    setLoading(true);
    fetchSub();
  }, [fetchSub]);

  // Realtime: refetch whenever this user's subscriptions change.
  // Channel name includes a per-instance suffix so multiple components using
  // this hook don't collide on the same channel name.
  const instanceIdRef = useRef(Math.random().toString(36).slice(2));
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`subs-${user.id}-${instanceIdRef.current}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => fetchSubRef.current(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return { loading, refresh: fetchSub, ...deriveState(sub) };
}
